import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  authApi,
  tryParseRegisterResponseFromApiError,
  memberApi,
  venuesApi,
  getToken,
  removeToken,
  ApiError,
} from '@/lib/api';
import type { User, LoginRequest, MemberRegisterRequest, VenueRegisterRequest, MemberProfile } from '@/types/api';
import type { VerificationChannel } from '@/lib/api/auth';
import { clearVerificationGateSkips } from '@/constants/verificationGate';
import { disconnectChatRealtime } from '@/lib/chat/chatRealtime';
import { isAdminEmbed, onAdminEmbedSession } from '@/lib/adminEmbed';

// ─── State shape ────────────────────────────────────────────────────────────

interface AuthState {
  user: User | null;
  isLoading: boolean;
  /** False until persist rehydration + `initialize()` have finished (avoids flash redirects on refresh). */
  sessionReady: boolean;
  error: string | null;
  pendingVerification: { userId: string; phone: string } | null;
  pendingEmailVerification: boolean;

  // Computed
  isAuthenticated: boolean;

  // Actions
  initialize: () => Promise<void>;
  login: (credentials: LoginRequest) => Promise<User>;
  registerMember: (data: MemberRegisterRequest) => Promise<{
    requiresVerification: boolean;
    requiresEmailVerification: boolean;
    userId?: string;
  }>;
  registerVenue: (data: VenueRegisterRequest) => Promise<{ requiresVerification: boolean; userId?: string; requiresEmailVerification?: boolean }>;
  verifyPhone: (code: string) => Promise<void>;
  resendVerificationCode: (channel?: VerificationChannel) => Promise<VerificationChannel>;
  resendEmailVerification: () => Promise<void>;
  checkEmailVerified: () => Promise<boolean>;
  clearPendingVerification: () => void;
  logout: () => void;
  clearError: () => void;
  refreshUser: () => Promise<void>;
  /** After POST /member/continue-register; backend may not echo accountTypeId on GET /member immediately. */
  applyMemberAccountTypeId: (accountTypeId: number) => void;
  /**
   * After PUT /member/update-info changed the login email/phone: the API clears that value's
   * verification, so mirror it locally — the badges drop to "Not verified" and the app can send the
   * member straight to /verify-email or /verify-phone for the new value.
   */
  applyContactChange: (change: {
    email?: string;
    phone?: string;
    emailChanged?: boolean;
    phoneChanged?: boolean;
  }) => void;
  // Internal helpers (not exported as public API)
  _setUser: (user: User | null) => void;
  _normalizeUser: (raw: User | null) => User | null;
  _fetchCurrentUser: (knownType?: string) => Promise<User>;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function normalizeMember(raw: MemberProfile | undefined | null): MemberProfile | undefined {
  if (raw == null) return undefined;
  const m = raw as MemberProfile & { account_type_id?: number | null };
  const accountTypeId = raw.accountTypeId ?? m.account_type_id ?? undefined;
  return { ...raw, accountTypeId };
}

/** If GET /member omits member.accountTypeId, keep the last known value for the same user (e.g. after continue-register). */
function mergePreservedMemberAccountTypeId(prev: User | null, next: User | null): User | null {
  if (!next || next.accountType !== 'member') return next;
  const sameUser = prev != null && prev.id === next.id;
  const prevId =
    sameUser && prev.accountType === 'member' ? prev.member?.accountTypeId : undefined;
  const nextId = next.member?.accountTypeId;
  if (prevId != null && nextId == null) {
    return { ...next, member: { ...next.member, accountTypeId: prevId } };
  }
  return next;
}

/** Phone OTP screen needs this when the member is logged in but phone is not verified yet. */
function memberPhonePending(user: User | null): { userId: string; phone: string } | null {
  if (!user || user.accountType !== 'member') return null;
  if (!user.emailVerifiedAt) return null;
  if (user.phoneVerifiedAt) return null;
  if (!user.phone || !user.id) return null;
  return { userId: String(user.id), phone: user.phone };
}

function normalizeUser(rawUser: User | null): User | null {
  if (!rawUser) return null;
  const u = rawUser as User & {
    email_verified_at?: string | null;
    phone_verified_at?: string | null;
    created_at?: string;
    updated_at?: string;
    account_type?: User['accountType'];
    account_type_id?: number | null;
    accountTypeId?: number | null;
    completed?: boolean;
  };
  const memberNormalized = normalizeMember(rawUser.member);
  const rootAccountTypeId =
    typeof u.accountTypeId === 'number'
      ? u.accountTypeId
      : typeof u.account_type_id === 'number'
        ? u.account_type_id
        : undefined;
  const mergedMemberId = memberNormalized?.accountTypeId ?? rootAccountTypeId;
  const memberOut =
    memberNormalized !== undefined || mergedMemberId !== undefined
      ? {
          ...rawUser.member,
          ...memberNormalized,
          ...(mergedMemberId !== undefined ? { accountTypeId: mergedMemberId } : {}),
        }
      : undefined;

  return {
    ...rawUser,
    completed:
      typeof rawUser.completed === 'boolean'
        ? rawUser.completed
        : typeof u.completed === 'boolean'
          ? u.completed
          : undefined,
    emailVerifiedAt: rawUser.emailVerifiedAt ?? u.email_verified_at ?? undefined,
    phoneVerifiedAt: rawUser.phoneVerifiedAt ?? u.phone_verified_at ?? undefined,
    createdAt: rawUser.createdAt ?? u.created_at,
    updatedAt: rawUser.updatedAt ?? u.updated_at,
    accountType: rawUser.accountType ?? u.account_type ?? rawUser.accountType,
    ...(memberOut ? { member: memberOut } : {}),
  };
}

function normalizeVenueSessionUser(raw: User): User {
  return normalizeUser({
    ...raw,
    accountType: 'venue',
  }) as User;
}

async function fetchVenueUserProfile(cachedUser?: User | null): Promise<User> {
  // `/me` is account-type agnostic and always resolves for any authenticated user,
  // unlike `/venue` which 404s until the venue owner has created their venue profile.
  // Without this, a brand-new venue account with no venue row yet could never resolve
  // emailVerifiedAt and would be stuck bouncing to /verify-email indefinitely.
  const me = await memberApi.getMe().catch(() => null);

  let venue: Awaited<ReturnType<typeof venuesApi.getMyVenue>> | null = null;
  try {
    venue = await venuesApi.getMyVenue();
  } catch {
    venue = null;
  }

  const venueAny = (venue ?? {}) as unknown as Record<string, unknown>;
  const userFromVenue = venueAny.user as Record<string, unknown> | undefined;

  let emailVerifiedAt: string | undefined = cachedUser?.emailVerifiedAt ?? me?.emailVerifiedAt;
  if (!emailVerifiedAt && userFromVenue?.email_verified_at) emailVerifiedAt = userFromVenue.email_verified_at as string;
  else if (!emailVerifiedAt && userFromVenue?.emailVerifiedAt) emailVerifiedAt = userFromVenue.emailVerifiedAt as string;
  else if (!emailVerifiedAt && venueAny.email_verified_at) emailVerifiedAt = venueAny.email_verified_at as string;
  else if (!emailVerifiedAt && venueAny.emailVerifiedAt) emailVerifiedAt = venueAny.emailVerifiedAt as string;

  const userId = cachedUser?.id ?? me?.id ?? (userFromVenue?.id as string) ?? (venueAny.userId as string) ?? venue?.id;

  return normalizeUser({
    ...(cachedUser ?? {}),
    id: userId,
    name: venue?.name ?? cachedUser?.name ?? me?.name ?? '',
    // Login values come from the user row (`/me`). `venue.email`/`venue.phone` are the listing's
    // public contact (e.g. where submitted forms go) and must never stand in for the login.
    email: me?.email ?? cachedUser?.email ?? '',
    phone: me?.phone ?? cachedUser?.phone ?? '',
    emailVerifiedAt,
    accountType: 'venue',
  } as User) as User;
}

async function fetchMemberUserProfile(): Promise<User> {
  const info = await memberApi.getInfo();
  try {
    const me = await memberApi.getMe();
    return normalizeUser({
      ...info,
      ...me,
      accountType: 'member',
      completed:
        typeof me.completed === 'boolean'
          ? me.completed
          : typeof (info as User).completed === 'boolean'
            ? (info as User).completed
            : undefined,
    } as User) as User;
  } catch {
    return normalizeUser(info) as User;
  }
}

/**
 * Load session user. Venue owners must not fall through to GET /member (that marks them
 * as `accountType: member` and triggers /member/continue-register).
 *
 * When the account type isn't already known (e.g. store hasn't rehydrated yet on page
 * load), ask `/me` — it's account-type agnostic and its `accountType` field is authoritative,
 * unlike guessing from whether `/venue` 404s (a member has no venue row either, so that
 * always "succeeds" and would misclassify every member as a venue).
 */
async function fetchCurrentUser(knownType?: string, cachedUser?: User | null): Promise<User> {
  let type = knownType ?? cachedUser?.accountType;

  if (!type) {
    const me = await memberApi.getMe();
    type = me.accountType;
  }

  if (type === 'member') {
    return fetchMemberUserProfile();
  }

  return fetchVenueUserProfile(cachedUser);
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isLoading: typeof window !== 'undefined' && !!getToken(),
      sessionReady: false,
      error: null,
      pendingVerification: null,
      pendingEmailVerification: false,

      get isAuthenticated() {
        return !!get().user;
      },

      _normalizeUser: normalizeUser,

      _setUser: (user) => set({ user }),

      _fetchCurrentUser: (knownType) => fetchCurrentUser(knownType, get().user),

      // ── Initialize (call on app mount) ──
      initialize: async () => {
        const token = getToken();
        // Admin member editor: become the member the dashboard hands us (API swaps the user via X-Member-Id).
        if (isAdminEmbed && new URLSearchParams(window.location.search).has('asMember')) {
          set({ user: null, isLoading: true, sessionReady: false });
          onAdminEmbedSession(async (s) => {
            if (!s.memberId) return;
            try {
              set({ user: normalizeUser(await fetchMemberUserProfile()), isLoading: false, sessionReady: true });
            } catch {
              set({ user: null, isLoading: false, sessionReady: true });
            }
          });
          return;
        }
        // Admin editor iframe uses the dashboard's session, never this site's login.
        if (!token || isAdminEmbed) {
          set({ isLoading: false, sessionReady: true });
          return;
        }

        set({ isLoading: true, sessionReady: false });
        try {
          const prev = get().user;
          const userData = await fetchCurrentUser(get().user?.accountType, get().user);
          const u = mergePreservedMemberAccountTypeId(prev, normalizeUser(userData));
          set({ user: u, pendingVerification: memberPhonePending(u), isLoading: false, sessionReady: true });
        } catch {
          removeToken();
          set({ user: null, isLoading: false, sessionReady: true });
        }
      },

      // ── Login ──
      login: async (credentials) => {
        set({ error: null, isLoading: true });
        try {
          const response = await authApi.login(credentials);
          const userData = response.user || (response as unknown as { data?: { user?: User } }).data?.user;

          const processUser = async (raw: User): Promise<User> => {
            const normalized = normalizeUser(raw) as User;
            // Listaa sign-in is members-only; reject venues before `user` is set so no
            // "already authenticated" redirect fires.
            if (normalized.accountType === 'venue') {
              authApi.logout();
              throw new Error('This is a venue account. Please sign in at https://venuelista.com/login');
            }
            let merged = normalized;
            try {
              const full = await fetchCurrentUser(normalized.accountType, normalized);
              merged = normalizeUser({
                ...normalized,
                ...full,
                accountType: normalized.accountType,
                emailVerifiedAt: full.emailVerifiedAt || normalized.emailVerifiedAt,
                phoneVerifiedAt: full.phoneVerifiedAt || normalized.phoneVerifiedAt,
              }) as User;
            } catch {
              merged = normalized;
            }
            merged = mergePreservedMemberAccountTypeId(get().user, merged) ?? merged;
            set({ user: merged, pendingVerification: memberPhonePending(merged) });
            return merged;
          };

          if (userData) {
            return await processUser(userData);
          }

          const maybeUser = response as unknown as User;
          if (maybeUser.id && maybeUser.email) {
            return await processUser(maybeUser);
          }

          throw new Error('Login response did not include user data');
        } catch (err) {
          const locked = tryParseRegisterResponseFromApiError(err);
          if (locked && normalizeUser(locked.user)?.accountType === 'venue') {
            authApi.logout();
            err = new Error('This is a venue account. Please sign in at https://venuelista.com/login');
          }
          // The API replies 423 (not a real auth failure) with a token + user for an
          // unverified account, so the shopper can still be signed in pending verification
          // instead of being told login failed outright.
          const recovered = tryParseRegisterResponseFromApiError(err);
          if (recovered) {
            const normalized = normalizeUser(recovered.user) as User;
            const merged = mergePreservedMemberAccountTypeId(get().user, normalized) ?? normalized;
            set({ user: merged, pendingVerification: null, pendingEmailVerification: true });
            return merged;
          }
          const message = err instanceof Error ? err.message : 'Login failed';
          set({ error: message });
          throw err;
        } finally {
          set({ isLoading: false });
        }
      },

      // ── Register Member ──
      registerMember: async (data) => {
        set({ error: null, isLoading: true });
        try {
          const response = await authApi.registerMember(data);
          const normalized = normalizeUser(response.user) as User;

          if (response.requiresEmailVerification) {
            try {
              await authApi.memberCheckEmail({ email: data.email });
            } catch {
              // User can still use verify-email page + resend
            }
            set({ user: normalized, pendingVerification: null, pendingEmailVerification: true });
            return {
              requiresEmailVerification: true,
              requiresVerification: false,
              userId: response.user.id,
            };
          }

          if (response.user && !response.user.phoneVerifiedAt) {
            set({
              pendingVerification: { userId: response.user.id, phone: data.phone },
              user: normalized,
            });
            return {
              requiresEmailVerification: false,
              requiresVerification: true,
              userId: response.user.id,
            };
          }

          set({ user: normalized, pendingVerification: memberPhonePending(normalized) });
          return { requiresEmailVerification: false, requiresVerification: false };
        } catch (err) {
          const recovered = tryParseRegisterResponseFromApiError(err);
          if (recovered?.requiresEmailVerification) {
            const normalized = normalizeUser(recovered.user);
            set({ user: normalized, pendingVerification: null, pendingEmailVerification: true });
            try {
              await authApi.memberCheckEmail({ email: data.email });
            } catch {
              /* verify-email page can resend */
            }
            return {
              requiresEmailVerification: true,
              requiresVerification: false,
              userId: recovered.user.id,
            };
          }
          const message = err instanceof ApiError
            ? err.validationMessage
            : err instanceof Error ? err.message : 'Registration failed';
          set({ error: message });
          throw err;
        } finally {
          set({ isLoading: false });
        }
      },

      // ── Register Venue ──
      registerVenue: async (data) => {
        set({ error: null, isLoading: true });
        try {
          const response = await authApi.registerVenue(data);
          const normalized = normalizeVenueSessionUser(response.user);
          set({ user: normalized });

          if (response.requiresEmailVerification) {
            set({ pendingEmailVerification: true });
          }

          return {
            requiresVerification: false,
            requiresEmailVerification: response.requiresEmailVerification,
          };
        } catch (err) {
          const recovered = tryParseRegisterResponseFromApiError(err);
          if (recovered) {
            const normalized = normalizeVenueSessionUser(recovered.user);
            set({ user: normalized });
            if (recovered.requiresEmailVerification) {
              set({ pendingEmailVerification: true });
            }
            return {
              requiresVerification: false,
              requiresEmailVerification: recovered.requiresEmailVerification,
            };
          }
          const rawMessage =
            err instanceof ApiError
              ? err.validationMessage || err.message
              : err instanceof Error
                ? err.message
                : 'Registration failed';
          const message = rawMessage.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
          set({ error: message });
          throw err;
        } finally {
          set({ isLoading: false });
        }
      },

      // ── Verify Phone ──
      verifyPhone: async (code) => {
        const { pendingVerification, user } = get();
        const id = pendingVerification?.userId ?? (user?.id != null ? String(user.id) : null);
        if (!id) throw new Error('No pending verification');

        set({ error: null, isLoading: true });
        try {
          await authApi.verifyPhone({ code, id });
          const prev = get().user;
          const userData = await fetchCurrentUser(user?.accountType, user);
          const next = mergePreservedMemberAccountTypeId(prev, normalizeUser(userData));
          set({ user: next, pendingVerification: memberPhonePending(next) });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Verification failed';
          set({ error: message });
          throw err;
        } finally {
          set({ isLoading: false });
        }
      },

      resendVerificationCode: async (channel) => {
        set({ error: null });
        const { pendingVerification, user } = get();
        const id = pendingVerification?.userId ?? user?.id;
        if (id == null || id === '') {
          const msg = 'No verification session; cannot resend SMS.';
          set({ error: msg });
          throw new Error(msg);
        }
        try {
          const res = await authApi.resendVerifySms(String(id), channel);
          return res?.channel === 'whatsapp' ? 'whatsapp' : 'sms';
        } catch (err) {
          const message = err instanceof ApiError
            ? err.validationMessage
            : err instanceof Error ? err.message : 'Failed to resend verification SMS';
          set({ error: message });
          throw err;
        }
      },

      resendEmailVerification: async () => {
        set({ error: null });
        const id = get().user?.id;
        if (id == null || id === '') {
          const msg = 'Not signed in; cannot resend verification email.';
          set({ error: msg });
          throw new Error(msg);
        }
        try {
          await authApi.resendEmailVerification(String(id));
        } catch (err) {
          const message = err instanceof ApiError
            ? err.validationMessage
            : err instanceof Error ? err.message : 'Failed to resend verification email';
          set({ error: message });
          throw err;
        }
      },

      checkEmailVerified: async () => {
        const { user: prev } = get();
        try {
          const userData = await fetchCurrentUser(prev?.accountType, prev);
          const normalized = mergePreservedMemberAccountTypeId(prev, normalizeUser(userData));
          set({
            user: normalized,
            pendingEmailVerification: false,
            pendingVerification: memberPhonePending(normalized),
          });
          if (normalized?.emailVerifiedAt) {
            return true;
          }
          return false;
        } catch { return false; }
      },

      clearPendingVerification: () => set({ pendingVerification: null }),

      logout: () => {
        clearVerificationGateSkips();
        // Mark the member offline (POST /member/chat/presence) while the auth
        // token is still valid — removeToken() below would otherwise make the
        // presence request unauthenticated and silently fail.
        disconnectChatRealtime();
        authApi.logout();
        set({ user: null, error: null, pendingVerification: null, pendingEmailVerification: false });
      },

      clearError: () => set({ error: null }),

      applyMemberAccountTypeId: (accountTypeId: number) => {
        const u = get().user;
        if (!u || u.accountType !== 'member') return;
        set({
          user: {
            ...u,
            member: { ...u.member, accountTypeId },
          },
        });
      },

      applyContactChange: (change) => {
        const u = get().user;
        if (!u) return;

        const next: User = {
          ...u,
          ...(change.email !== undefined ? { email: change.email } : {}),
          ...(change.phone !== undefined ? { phone: change.phone } : {}),
        };

        if (change.emailChanged) next.emailVerifiedAt = undefined;
        if (change.phoneChanged) next.phoneVerifiedAt = undefined;

        set({
          user: next,
          // Phone OTP starts only once the email is verified — same order as RootLayout's gate.
          pendingVerification: memberPhonePending(next),
          pendingEmailVerification: Boolean(change.emailChanged) || get().pendingEmailVerification,
        });
      },

      refreshUser: async () => {
        if (!getToken()) return;
        const prev = get().user;
        try {
          const userData = await fetchCurrentUser(prev?.accountType, prev);
          const u = mergePreservedMemberAccountTypeId(prev, normalizeUser(userData));
          set({ user: u, pendingVerification: memberPhonePending(u), pendingEmailVerification: false });
        } catch (err) {
          // A 423 here means "email not verified", not "session invalid" — the token is
          // still good. Keep the existing session and flag it instead of logging the user out.
          if (err instanceof ApiError && err.status === 423) {
            set({ pendingEmailVerification: true });
            return;
          }
          removeToken();
          set({ user: null });
          console.error('Failed to refresh user:', err);
        }
      },
    }),
    {
      name: 'venuelista_auth',
      partialize: (state) => ({ user: state.user }),
      // Embedded admin editor must never overwrite the real listaa.com login in other tabs.
      storage: createJSONStorage(() => (isAdminEmbed ? sessionStorage : localStorage)),
    }
  )
);

// ─── Selector helpers (use these in components to avoid full re-renders) ───

export const selectUser = (s: AuthState) => s.user;
export const selectIsAuthenticated = (s: AuthState) => !!s.user;
export const selectIsLoading = (s: AuthState) => s.isLoading;
export const selectError = (s: AuthState) => s.error;
export const selectAccountType = (s: AuthState) => s.user?.accountType;
